import { useEffect, useMemo, useState } from "react";

import type {
  ApprovalEntryResponseSchema,
  VinciCandidateSchema,
} from "@/api/generated/types.gen";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCreateAccessEntry,
  useDeleteAccessEntry,
  useUpdateAccessEntry,
} from "@/hooks/mutations/use-access-list";
import { useAccessList, useVinciSearch } from "@/hooks/queries/use-access-list";
import { useMe } from "@/hooks/queries/use-me";

/** Debounce a changing value by `delay` ms. */
const useDebounced = <T,>(value: T, delay = 300): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const statusVariant = (status: string) =>
  status === "active" ? "success" : "warning";

const AddUserForm = () => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<VinciCandidateSchema | null>(null);
  const [accessLevel, setAccessLevel] = useState("");
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebounced(query);
  const { data: candidates = [], isFetching } = useVinciSearch({
    q: debouncedQuery,
    enabled: !selected,
  });
  const createEntry = useCreateAccessEntry();

  const handleAdd = async () => {
    if (!selected) return;
    setError(null);
    try {
      await createEntry.mutateAsync({
        email: selected.email,
        user_type: selected.user_type,
        access_level: accessLevel || null,
      });
      setSelected(null);
      setQuery("");
      setAccessLevel("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add user.");
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 className="text-sm font-semibold">Add a user</h2>

      {selected ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-col">
            <span className="font-medium">{selected.name || selected.email}</span>
            <span className="text-muted-foreground text-sm">
              {selected.email} · {selected.user_type}
            </span>
          </div>
          {selected.is_shared_inbox && (
            <Badge variant="warning">Shared inbox — grants everyone on it</Badge>
          )}
          <Input
            className="max-w-[200px]"
            placeholder="Access level (optional)"
            value={accessLevel}
            onChange={(e) => setAccessLevel(e.target.value)}
          />
          <Button onClick={handleAdd} disabled={createEntry.isPending}>
            {createEntry.isPending ? "Adding…" : "Add to approval list"}
          </Button>
          <button
            type="button"
            className="text-sm underline"
            onClick={() => setSelected(null)}
          >
            Change
          </button>
        </div>
      ) : (
        <div className="relative flex flex-col gap-1">
          <Input
            placeholder="Search Vinci by name or email (min 2 chars)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {debouncedQuery.trim().length >= 2 && (
            <div className="max-h-64 overflow-auto rounded-md border">
              {isFetching && (
                <div className="text-muted-foreground p-2 text-sm">Searching…</div>
              )}
              {!isFetching && candidates.length === 0 && (
                <div className="text-muted-foreground p-2 text-sm">
                  No matching Vinci users.
                </div>
              )}
              {candidates.map((c) => (
                <button
                  type="button"
                  key={`${c.user_type}-${c.vinci_id}`}
                  className="hover:bg-muted flex w-full items-center justify-between gap-2 p-2 text-left"
                  onClick={() => setSelected(c)}
                >
                  <span>
                    <span className="font-medium">{c.name || c.email}</span>{" "}
                    <span className="text-muted-foreground text-sm">
                      {c.email} · {c.user_type}
                    </span>
                  </span>
                  {c.is_shared_inbox && <Badge variant="warning">shared</Badge>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

const EntryRow = ({ entry }: { entry: ApprovalEntryResponseSchema }) => {
  const update = useUpdateAccessEntry();
  const remove = useDeleteAccessEntry();
  const [accessLevel, setAccessLevel] = useState(entry.access_level ?? "");

  const isSuspended = entry.status === "suspended";
  const dirty = (entry.access_level ?? "") !== accessLevel;

  return (
    <TableRow>
      <TableCell>
        {entry.email}
        {entry.is_shared_inbox && (
          <Badge variant="warning" className="ml-2">
            shared
          </Badge>
        )}
      </TableCell>
      <TableCell>{entry.user_type}</TableCell>
      <TableCell>
        <Badge variant={statusVariant(entry.status)}>{entry.status}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Input
            className="max-w-[160px]"
            value={accessLevel}
            placeholder="—"
            onChange={(e) => setAccessLevel(e.target.value)}
          />
          {dirty && (
            <Button
              variant="outline"
              onClick={() =>
                update.mutate({
                  entryId: entry.id,
                  body: { access_level: accessLevel || null },
                })
              }
              disabled={update.isPending}
            >
              Save
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() =>
              update.mutate({
                entryId: entry.id,
                body: { status: isSuspended ? "active" : "suspended" },
              })
            }
            disabled={update.isPending}
          >
            {isSuspended ? "Activate" : "Suspend"}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (window.confirm(`Remove ${entry.email} from the approval list?`)) {
                remove.mutate(entry.id);
              }
            }}
            disabled={remove.isPending}
          >
            Remove
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const AccessControlPage = () => {
  const { data: me, isLoading: meLoading } = useMe();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);

  const isAdmin = Boolean(me?.user?.is_admin);
  const { data: entries = [], isLoading } = useAccessList({
    status: statusFilter || undefined,
    q: debouncedSearch || undefined,
    enabled: isAdmin,
  });

  const heading = useMemo(
    () => `Access Control (${entries.length})`,
    [entries.length]
  );

  if (meLoading) {
    return <div className="p-6">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="text-muted-foreground p-6">
        You don’t have access to this page.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <h1 className="text-xl font-semibold">{heading}</h1>
      <p className="text-muted-foreground text-sm">
        Approved users can sign in to Report Builder with an email code. Being in
        Vinci is necessary but not sufficient — this list is the gate.
      </p>

      <AddUserForm />

      <div className="flex items-center gap-3">
        <Input
          className="max-w-[280px]"
          placeholder="Filter by email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="h-9 rounded-md border px-2 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>User type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Access level</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={5}>Loading…</TableCell>
            </TableRow>
          ) : entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>No entries yet.</TableCell>
            </TableRow>
          ) : (
            entries.map((entry) => <EntryRow key={entry.id} entry={entry} />)
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default AccessControlPage;
